import { useCallback, useEffect, useRef, useState } from 'react';
import { UserPlus, X, ArrowUpToLine, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';

// Late-arrival match (plan A, owner 2026-09-30) — "the app proposes, the
// coordinator decides". The Courts tab shows the prompt once most regulars
// have 2+ games and someone who checked in late has no game coming soon; the
// preview builds ONE match (newcomers first, fairest fillers, balanced), the
// coordinator can Change a filler, then Push to front. Once per newcomer.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === 'string' ? d : null))) || fallback;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function LateArrivalPrompt({ state, divisionName, onCreate, onMinimize }) {
  if (!state?.show) return null;
  const waiting = state.waiting || [];
  const etas = waiting.map(w => w.eta_minutes).filter(v => v != null);
  const worst = etas.length ? Math.max(...etas) : null;
  const n = waiting.length;
  const per = state.per_match || 4;
  return (
    <div data-testid="late-arrival-prompt" className="relative overflow-hidden rounded-xl flex flex-wrap items-center gap-3 px-4 py-3.5"
      style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.09), rgba(245,158,11,0.02) 60%, transparent), #141414',
               border: '1px solid #F59E0B55', boxShadow: '0 10px 36px -14px rgba(245,158,11,0.55)' }}>
      <span className="absolute left-0 top-0 bottom-0" style={{ width: 5, background: '#F59E0B' }} />
      <span className="flex items-center justify-center rounded-lg shrink-0 ml-1"
        style={{ width: 38, height: 38, background: '#F59E0B1F', border: '1px solid #F59E0B44' }}>
        <UserPlus className="h-5 w-5" style={{ color: '#F59E0B' }} />
      </span>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-extrabold" style={{ color: '#fff' }}>
          {plural(n, 'new player', 'new players')} waiting{divisionName ? ` — ${divisionName}` : ''}
          {worst != null
            ? <> — first game in <span style={{ color: '#F59E0B' }}>~{worst} min</span> without help</>
            : <> — <span style={{ color: '#F59E0B' }}>no game lined up</span> yet</>}
        </p>
        <p className="text-xs mt-0.5" style={{ color: '#A3A3A3' }}>
          They checked in after the queue was drawn. {state.regulars_ready} of {state.regulars} regulars already have 2+ games.
          {n > per && ` One match takes ${per} — the prompt comes back for the other ${n - per}.`}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {waiting.map(w => (
            <span key={w.id} className="text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: '#0B0B0B', border: '1px solid #2A2A2A', color: '#E5E5E5' }}>{w.name}</span>
          ))}
        </div>
      </div>
      <div className="flex gap-2 w-full sm:w-auto">
        <button data-testid="late-arrival-minimize" onClick={onMinimize}
          title="Shrink to a pill at the top — tap it when you're ready"
          className="px-3 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1" style={{ color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
          <Minus className="h-3.5 w-3.5" /> Minimize
        </button>
        <button data-testid="late-arrival-create" onClick={onCreate}
          className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-black uppercase"
          style={{ background: '#F59E0B', color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
                   boxShadow: '0 6px 22px -6px rgba(245,158,11,0.75)' }}>
          Create late-arrival match
        </button>
      </div>
    </div>
  );
}

function Seat({ p, open, onChange }) {
  const why = p.newcomer
    ? 'Checked in late · 0 games'
    : `${plural(p.games, 'game', 'games')} · ${p.next_position ? `next game #${p.next_position}${p.next_eta_minutes != null ? ` (~${p.next_eta_minutes} min)` : ''}` : 'not in the queue'}`;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5" style={{ borderTop: '1px solid #1B1B1B' }}>
      <div className="min-w-0">
        <p className="text-sm font-extrabold flex items-center gap-2 flex-wrap" style={{ color: '#fff' }}>
          {p.name}
          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded"
            style={p.newcomer ? { background: '#22C55E22', color: '#4ADE80', border: '1px solid #22C55E55' }
              : { background: '#FFFFFF0D', color: '#BDBDBD', border: '1px solid #2F2F2F' }}>
            {p.newcomer ? 'NEW' : 'FILLER'}
          </span>
          {p.level != null && (
            <span className="text-xs px-1.5 rounded" style={{ fontFamily: 'Oswald, sans-serif', color: '#D4D4D4', background: '#0A0A0A', border: '1px solid #262626' }}>
              {Number(p.level).toFixed(1)}
            </span>
          )}
        </p>
        <p className="text-xs mt-0.5" style={{ color: '#8A8A8A' }}>{why}</p>
      </div>
      {!p.newcomer && (
        <button data-testid={`late-change-${p.id}`} onClick={onChange} aria-expanded={open}
          className="shrink-0 text-xs font-extrabold px-2.5 py-1.5 rounded-lg"
          style={{ background: '#161616', color: open ? '#F59E0B' : '#E5E5E5', border: `1px solid ${open ? '#F59E0B' : '#3A3A3A'}` }}>
          Change
        </button>
      )}
    </div>
  );
}

export function LateArrivalModal({ sessionId, divisionId, requirePin, clearPin, onClose, onPushed }) {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);  // filler id being changed
  const [err, setErr] = useState('');

  const load = useCallback(async (fillers) => {
    setErr('');
    try {
      const r = await sportsApi.post(`/sports/open-play/${sessionId}/late-arrival/preview`,
        fillers ? { division_id: divisionId, fillers } : { division_id: divisionId });
      setPv(r.data);
      setPicker(null);
    } catch (e) {
      setErr(errMsg(e, 'Could not build the match'));
    }
  }, [sessionId, divisionId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const fillers = pv ? [...pv.team_a, ...pv.team_b].filter(p => !p.newcomer).map(p => p.id) : [];
  const swap = (from, to) => load(fillers.map(f => (f === from ? to : f)));
  const diff = pv ? Math.abs(pv.level_a - pv.level_b) : 0;

  const push = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setBusy(true);
    try {
      await sportsApi.post(`/sports/open-play/${sessionId}/late-arrival/push`, {
        division_id: divisionId, pin,
        team_a: pv.team_a.map(p => p.id), team_b: pv.team_b.map(p => p.id),
      });
      toast.success(pv.more_waiting
        ? `Pushed to #1 — ${plural(pv.more_waiting, 'more new player', 'more new players')} still waiting`
        : 'Pushed to #1 — plays on the next free court');
      onPushed();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not push the match'));
      if (e.response?.status === 409) load();
    } finally { setBusy(false); }
  };

  const team = (label, color, list, sum) => (
    <div className="rounded-xl overflow-hidden mb-2.5" style={{ border: '1px solid #242424' }}>
      <div className="flex items-center justify-between px-3 py-2 text-xs uppercase"
        style={{ fontFamily: 'Oswald, sans-serif', color, background: `${color}0D`, letterSpacing: 0.5 }}>
        {label}<span style={{ fontFamily: 'Inter, sans-serif', color: '#8A8A8A', textTransform: 'none' }}>{sum.toFixed(1)}</span>
      </div>
      {list.map(p => (
        <div key={p.id}>
          <Seat p={p} open={picker === p.id} onChange={() => setPicker(picker === p.id ? null : p.id)} />
          {picker === p.id && (
            <div data-testid="late-picker" className="mx-2 mb-2 rounded-lg p-1.5" style={{ background: '#0A0A0A', border: '1px solid #262626' }}>
              <p className="text-[10px] font-extrabold uppercase px-2 pt-1 pb-1.5" style={{ color: '#777', letterSpacing: '0.12em' }}>Swap {p.name} for</p>
              {(pv.candidates || []).slice(0, 5).map(c => (
                <button key={c.id} data-testid={`late-pick-${c.id}`} onClick={() => swap(p.id, c.id)}
                  className="w-full flex items-center justify-between gap-2 text-left px-2 py-2 rounded-md hover:bg-[#1C1C1C]">
                  <span className="min-w-0">
                    <span className="block text-sm font-bold" style={{ color: '#E5E5E5' }}>{c.name}</span>
                    <span className="block text-[11px]" style={{ color: '#8A8A8A' }}>
                      {plural(c.games, 'game', 'games')} · {c.next_position ? `next game #${c.next_position}` : 'not in the queue'}
                    </span>
                  </span>
                  <span className="text-xs px-1.5 rounded shrink-0" style={{ fontFamily: 'Oswald, sans-serif', color: '#D4D4D4', border: '1px solid #262626' }}>
                    {Number(c.level).toFixed(1)}
                  </span>
                </button>
              ))}
              {(pv.not_offered?.on_court?.length > 0 || pv.not_offered?.up_next?.length > 0) && (
                <p className="text-[11px] px-2 pt-1.5" style={{ color: '#666' }}>
                  Not offered: {[pv.not_offered.on_court.length ? `${pv.not_offered.on_court.join(', ')} (on court)` : null,
                    pv.not_offered.up_next.length ? `${pv.not_offered.up_next.join(', ')} (up next)` : null].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid="late-arrival-modal"
      style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="late-title"
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-4 sm:p-5"
        style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', boxShadow: '0 30px 80px #000, 0 0 0 1px #F59E0B22' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 id="late-title" className="text-lg font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>
            Late-arrival match
          </h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm mt-1 mb-3.5" style={{ color: '#A3A3A3' }}>
          Nothing starts yet. Check the players, then push the match to the front of Up next — it plays on the next free court.
        </p>
        {!pv && !err && <p className="text-sm py-6 text-center" style={{ color: '#737373' }}>Building the match…</p>}
        {pv && (
          <>
            {team('Team A', '#FF6B76', pv.team_a, pv.level_a)}
            {team('Team B', '#6EA8FF', pv.team_b, pv.level_b)}
            <div className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs mb-3"
              style={{ background: '#0B0B0B', border: '1px solid #1E1E1E', color: '#BDBDBD' }}>
              Level balance <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{pv.level_a.toFixed(1)}</b> vs <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{pv.level_b.toFixed(1)}</b>
              <span className="ml-auto font-extrabold" style={{ color: diff <= 0.5 ? '#4ADE80' : diff <= 1 ? '#4ADE80' : '#FCD34D' }}>
                {diff <= 0.5 ? 'Even' : diff <= 1 ? 'Close' : 'Uneven — try Change'}
              </span>
            </div>
            <ul className="text-xs space-y-1 mb-3 pl-4 list-disc" style={{ color: '#8A8A8A' }}>
              <li>Fillers = fewest games first, then the ones furthest from their next game. Nobody on court or due up next.</li>
              <li>Fillers keep their queued games — they sit a set sooner later, so it evens out.</li>
              <li>Two new players go one per team. One time per new player; after this game they join the normal rotation.</li>
            </ul>
          </>
        )}
        {err && <p data-testid="late-error" className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}
        <div className="flex gap-2 justify-end sticky bottom-0 pt-2" style={{ background: '#0F0F0F' }}>
          <button data-testid="late-cancel" onClick={onClose} disabled={busy}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={{ color: '#E5E5E5', border: '1px solid #2A2A2A', background: '#161616' }}>
            Cancel
          </button>
          <button data-testid="late-push" onClick={push} disabled={busy || !pv}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase inline-flex items-center justify-center gap-1.5"
            style={{ background: '#F59E0B', color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, opacity: busy || !pv ? 0.6 : 1 }}>
            <ArrowUpToLine className="h-4 w-4" /> {busy ? 'Pushing…' : 'Push to front'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimized (or on another tab): a small amber pill in the console's status
// strip. Tapping it opens the preview straight away. A new late arrival
// bumps the count and pulses it once — it never jumps back open by itself.
export function LateArrivalPill({ state, divisionName, pulse, onOpen, onPulseEnd }) {
  // A timer, not animationend: with reduced motion (or a background tab) the
  // animation never ends and the pill would stay "new" forever.
  // (the callback is read from a ref: the console re-renders on every poll)
  const endRef = useRef(onPulseEnd);
  endRef.current = onPulseEnd;
  useEffect(() => {
    if (!pulse) return undefined;
    const t = setTimeout(() => endRef.current(), 2000);
    return () => clearTimeout(t);
  }, [pulse]);
  if (!state?.show) return null;
  const n = (state.waiting || []).length;
  return (
    <div className="inline-flex">
      <button data-testid="late-arrival-pill" onClick={onOpen}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold${pulse ? ' op-late-pulse' : ''}`}
        style={{ background: '#1A1206', color: '#FCD34D', border: '1px solid #F59E0B88' }}
        title="Open the late-arrival match">
        <UserPlus className="h-3.5 w-3.5 self-center" style={{ color: '#F59E0B' }} />
        <b style={{ fontFamily: 'Oswald, sans-serif', fontSize: 17, color: '#F59E0B' }}>{n}</b>
        late arrival{n === 1 ? '' : 's'}{divisionName ? ` · ${divisionName}` : ''} · Create match →
      </button>
    </div>
  );
}
